import React, { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { Asset } from 'contentful';
import { useSpring, animated, easings } from 'react-spring';

import { IFullBleedHeroFields } from '@/types/contentful';

import { getImageDataFromOptions } from '../../utils/contentful';
import { usePageDataContext } from '../../context/PageData';
import { TextWithOptions } from '../Text/TextWithOptions';

import PrivacyPolicyDisclaimer from 'src/components/PrivacyPolicyDisclaimer';

const CtaWidget = dynamic(
  () => import('../CtaWidget').then((mod) => mod.default),
  { ssr: true }
);
import GlImage from '../Image';
import AdditionalContent from '../AdditionalContent';
import { ModalRevealCTA } from '../ModalRevealCTA';
import { SocialShare } from '../LearningCenter/Components/ArticleHeader/SocialShare';

import cx from 'classnames';
import Image from 'next/image';
import { SmartText } from '../SmartText';
import { useVideo } from '~/hooks/useVideo';
import { captureError, trackRumEvent } from '../../utils/datadogRum';
import { MediaControlButton } from '../AtomicComponents/MediaControlButton';

export function FullBleedHero({
  headingText,
  headingTextOptions,
  desktopBackgroundImage,
  desktopBackgroundImageOptions,
  desktopForVideosDominantColor,
  mobileBackgroundImage,
  mobileBackgroundImageOptions,
  privacyPolicyDisclaimer,
  ctaWidget,
  additionalContent,
  returningUserHeadingText,
  shareIcons,
  animationType = 'opacity',
  animatedTextDecoration,
  badgeIcon,
  badgeText
}: IFullBleedHeroFields) {
  const { query: { gl_content, g_adgroupid, utm_source } = {}, isReturning } =
    usePageDataContext();

  // Check if assets are videos (based on content type)
  const isDesktopVideo =
    desktopBackgroundImage?.fields?.file?.contentType?.includes('mp4') ||
    desktopBackgroundImage?.fields?.file?.contentType?.includes('video');
  const isMobileVideo =
    mobileBackgroundImage?.fields?.file?.contentType?.includes('mp4') ||
    mobileBackgroundImage?.fields?.file?.contentType?.includes('video');

  const isDesktopImage =
    desktopBackgroundImage?.fields?.file?.contentType?.includes('image');
  const isMobileImage =
    mobileBackgroundImage?.fields?.file?.contentType?.includes('image');

  const desktopRawUrl = isDesktopVideo
    ? desktopBackgroundImage?.fields?.file?.url || ''
    : '';
  const mobileRawUrl = isMobileVideo
    ? mobileBackgroundImage?.fields?.file?.url || ''
    : '';

  // Infer sensible posters: use the non-video counterpart if available
  const desktopPosterUrl =
    isDesktopVideo && isMobileImage
      ? mobileBackgroundImage?.fields?.file?.url
      : undefined;
  const mobilePosterUrl =
    isMobileVideo && isDesktopImage
      ? desktopBackgroundImage?.fields?.file?.url
      : undefined;

  const desktopUrl = desktopRawUrl;
  const mobileUrl = mobileRawUrl;

  const { videoRef: desktopVideoRef, sourceUrl: desktopSourceUrl } = useVideo(
    desktopUrl,
    { manageVisibility: false, deferUntilVisible: true }
  );
  const { videoRef: mobileVideoRef, sourceUrl: mobileSourceUrl } = useVideo(
    mobileUrl,
    { manageVisibility: false, deferUntilVisible: true }
  );

  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const listener = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    const videos: HTMLVideoElement[] = [
      desktopVideoRef.current,
      mobileVideoRef.current
    ].filter(Boolean) as HTMLVideoElement[];
    if (!videos.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) {
      videos.forEach((v) => v.pause());
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) {
            if (!v.ended) {
              v.play()
                .then(() => {
                  setIsPlaying(true);
                  setIsEnded(false);
                  setAutoplayBlocked(false);
                })
                .catch((err: DOMException) => {
                  if (err.name === 'NotAllowedError') {
                    trackRumEvent('autoplay_blocked', {
                      page_url: window.location.href,
                      component: 'FullBleedHero',
                      element_id: v.id,
                      muted: v.muted
                    });
                    captureError(err);
                    setAutoplayBlocked(true);
                  }
                });
            }
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    videos.forEach((v) => io.observe(v));
    const onPlay = () => {
      setIsPlaying(true);
      setIsEnded(false);
    };
    const onPause = () => {
      setIsPlaying(false);
    };
    const onEnded = () => {
      setIsEnded(true);
      setIsPlaying(false);
    };
    videos.forEach((v) => {
      v.addEventListener('play', onPlay);
      v.addEventListener('pause', onPause);
      v.addEventListener('ended', onEnded);
    });
    return () => {
      io.disconnect();
      videos.forEach((v) => {
        v.removeEventListener('play', onPlay);
        v.removeEventListener('pause', onPause);
        v.removeEventListener('ended', onEnded);
      });
    };
  }, [desktopUrl, mobileUrl]);

  // Derive poster from the first frame if no poster is provided. JPEG + smaller canvas + idle to keep fast.
  useEffect(() => {
    const listeners: Array<{ el: HTMLVideoElement; fn: () => void }> = [];
    const MAX_POSTER_WIDTH = 480;
    const JPEG_QUALITY = 0.6;

    const makePoster = (video: HTMLVideoElement | null) => {
      if (!video || video.poster) return;

      const drawFirstFrame = () => {
        if (video.poster) return;
        const width = video.videoWidth || 1;
        const height = video.videoHeight || 1;
        if (!width || !height) return;
        const targetW = Math.min(MAX_POSTER_WIDTH, width);
        const targetH = Math.round((targetW / width) * height);
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const runEncode = () => {
          try {
            ctx.drawImage(video, 0, 0, targetW, targetH);
            const url = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
            if (url) {
              video.poster = url;
            }
          } catch (_e) {
            // Ignore cross-origin canvas errors
          }
        };
        // Use setTimeout(0) instead of requestIdleCallback for hero content:
        // requestIdleCallback may defer up to 500ms during page load, which
        // directly hurts LCP when the poster is the largest contentful element.
        setTimeout(runEncode, 0);
      };

      if (video.readyState >= 2) {
        drawFirstFrame();
      } else {
        const onLoaded = () => {
          drawFirstFrame();
          video.removeEventListener('loadeddata', onLoaded);
        };
        video.addEventListener('loadeddata', onLoaded);
        listeners.push({ el: video, fn: onLoaded });
      }
    };

    makePoster(desktopVideoRef.current);
    makePoster(mobileVideoRef.current);

    return () => {
      listeners.forEach(({ el, fn }) =>
        el.removeEventListener('loadeddata', fn)
      );
    };
  }, [desktopVideoRef, mobileVideoRef, desktopPosterUrl, mobilePosterUrl]);

  // Animation for headingText (only if animationType is 'opacity'). Kept short to improve LCP / element render delay.
  const headingProps = useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(20px)' },
    delay: animationType !== 'opacity' ? 0 : prefersReducedMotion ? 0 : 400,
    config: {
      duration: prefersReducedMotion ? 0 : 3000,
      easing: easings.easeOutExpo
    },
    immediate: animationType !== 'opacity' || prefersReducedMotion
  });

  // Animation for CTA and Privacy Policy (this will always use useSpring)
  const ctaProps = useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(20px)' },
    delay: animationType !== 'opacity' ? 0 : prefersReducedMotion ? 0 : 700,
    config: {
      duration: prefersReducedMotion ? 0 : 3000,
      easing: easings.easeOutExpo
    },
    immediate: animationType !== 'opacity' || prefersReducedMotion
  });

  // Determine the heading content
  const headingContent = (
    <animated.div style={headingProps}>
      <TextWithOptions
        as="h1"
        className={cx(
          'fullbleed-hero-headline',
          animatedTextDecoration
            ? 'fullbleed-hero-headline-animated'
            : 'fullbleed-hero-headline-default'
        )}
        data-testid="fullbleed-hero-headline"
        keyword={gl_content}
        channel={utm_source}
        adGroupId={g_adgroupid}
        fallback={
          (isReturning
            ? returningUserHeadingText ?? headingText
            : headingText) || ''
        }
        options={headingTextOptions}
        module="FullBleed Hero heading"
      />
    </animated.div>
  );

  // Preload poster image when from CMS so LCP is measured when poster is shown (video LCP optimization)
  const posterPreloadUrls = useMemo(() => {
    const toAbsoluteUrl = (url: string) =>
      url.startsWith('http') ? url : `https:${url}`;
    return [
      ...new Set(
        [desktopPosterUrl, mobilePosterUrl]
          .filter(Boolean)
          .map((u) => toAbsoluteUrl(u!))
      )
    ];
  }, [desktopPosterUrl, mobilePosterUrl]);

  return (
    <div
      data-blockid="full-bleed-hero"
      data-testid="full-bleed-hero"
      data-e2e="hero"
      className=""
    >
      {posterPreloadUrls.length > 0 && (
        <Head>
          {posterPreloadUrls.map((href) => (
            <link
              key={href}
              rel="preload"
              as="image"
              href={href}
              {...({
                fetchPriority: 'high'
              } as React.LinkHTMLAttributes<HTMLLinkElement>)}
            />
          ))}
        </Head>
      )}
      <div className="relative grid w-full desktop:max-h-[85vh] desktop:min-h-[650px] desktop:overflow-hidden">
        <MediaControlButton
          visible={autoplayBlocked}
          isPlaying={isPlaying}
          isEnded={isEnded}
          position="top-right"
          onClick={async () => {
            const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
            const target =
              (isDesktop ? desktopVideoRef.current : mobileVideoRef.current) ||
              desktopVideoRef.current ||
              mobileVideoRef.current;
            if (!target) return;
            try {
              if (isPlaying) {
                target.pause();
              } else {
                if (isEnded) target.currentTime = 0;
                await target.play();
              }
            } catch (err) {
              captureError(err as Error);
            }
          }}
        />
        {/* Desktop */}
        <div
          className={
            'hidden w-full max-w-[100vw] desktop:flex desktop:max-h-[85vh] desktop:min-h-[650px]'
          }
          style={{ gridArea: '1/1', background: desktopForVideosDominantColor }}
        >
          {isDesktopVideo ? (
            <video
              ref={desktopVideoRef}
              autoPlay
              muted
              playsInline
              controls={false}
              disablePictureInPicture
              preload="metadata"
              crossOrigin="anonymous"
              poster={desktopPosterUrl}
              id="desktop-video-background"
              className="h-full w-full object-cover object-center [@media(width>=1751px)]:object-contain"
            >
              <source src={desktopSourceUrl} type="video/mp4" />
            </video>
          ) : (
            <GlImage
              {...getImageDataFromOptions(
                gl_content,
                utm_source,
                g_adgroupid,
                desktopBackgroundImage as Asset,
                desktopBackgroundImageOptions
              )}
              className="aspect-[1440/700] h-full w-full object-top"
              loading="eager"
              heroImage={true}
              layout="fill"
              objectFit="cover"
              width={undefined}
              height={undefined}
              alt="desktop-background-image"
            />
          )}
        </div>
        {/* Mobile */}
        <div
          className="block max-h-[740px] tablet:max-h-[876px] desktop:hidden"
          style={{ gridArea: '1/1', background: desktopForVideosDominantColor }}
        >
          {isMobileVideo ? (
            <video
              ref={mobileVideoRef}
              autoPlay
              muted
              playsInline
              controls={false}
              disablePictureInPicture
              preload="metadata"
              crossOrigin="anonymous"
              poster={mobilePosterUrl}
              className="aspect-[828/1200] h-full w-full object-cover object-top [@media(width>=635px)]:mx-auto [@media(width>=635px)]:w-[635px] [@media(width>=635px)]:max-w-[635px]"
            >
              <source src={mobileSourceUrl} type="video/mp4" />
            </video>
          ) : (
            <div className="aspect-[1440/2096] h-full w-full object-fill object-center">
              <GlImage
                {...getImageDataFromOptions(
                  gl_content,
                  utm_source,
                  g_adgroupid,
                  mobileBackgroundImage as Asset,
                  mobileBackgroundImageOptions
                )}
                loading="eager"
                heroImage={true}
                layout="fill"
                objectFit="cover"
                objectPosition={'top'}
                alt="mobile-background-image"
              />
            </div>
          )}
        </div>
        <div
          style={{
            gridArea: '1/1',
            position: 'relative',
            placeItems: 'center',
            display: 'grid'
          }}
        >
          <div className="block-container block h-full w-full !justify-normal desktop:flex desktop:flex-col">
            <div className="relative mt-auto flex h-full w-full max-w-[100vw] px-5 pb-5 align-bottom tablet:px-10 tablet:pb-10 desktop:px-15 desktop:pb-15">
              {badgeIcon && (
                <div
                  className="
                  absolute top-14 hidden size-[120px] items-center justify-center
                  desktop:block
                  "
                >
                  <Image
                    src={badgeIcon?.fields?.file?.url}
                    width={badgeIcon?.fields?.file?.details?.image?.width}
                    height={badgeIcon?.fields?.file?.details?.image?.height}
                    alt="Full Bleed Badge"
                    className="absolute left-0 top-0 z-[1] h-full w-full animate-spin object-contain [animation-duration:20000ms]"
                    loading="eager"
                    priority
                  />
                  {badgeText && (
                    <SmartText>
                      <div className="typography-title-3 relative z-[2] mx-auto flex h-full w-[100px] items-center justify-center text-center font-medium leading-[115%]">
                        {badgeText}
                      </div>
                    </SmartText>
                  )}
                </div>
              )}
              <div className="w-full self-end desktop:max-w-[655px]">
                {headingContent}
                <animated.div
                  style={animationType === 'opacity' ? ctaProps : {}}
                >
                  {ctaWidget &&
                    ctaWidget.sys.contentType.sys.id ===
                      'registrationEntrypoint' && (
                      <div className="desktop:mb-0 desktop:max-w-[350px]">
                        <CtaWidget
                          width={'100%'}
                          {...ctaWidget?.fields}
                          sysId={ctaWidget?.sys?.contentType?.sys?.id}
                          id="fullwidth-hero-cta"
                          module="Hero"
                          isMobile={false}
                          responsive
                        />
                      </div>
                    )}
                  {ctaWidget &&
                    ctaWidget.sys.contentType.sys.id === 'modalRevealCta' && (
                      <div className="max-w-[350px] desktop:mb-0">
                        <ModalRevealCTA {...(ctaWidget.fields as any)} />
                      </div>
                    )}
                  {privacyPolicyDisclaimer && (
                    <div className="mt-2 max-w-[350px] tablet:max-w-[467px] desktop:max-w-[435px]">
                      <PrivacyPolicyDisclaimer
                        privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                        displayPrivacyPolicyIcon={false}
                        color={'white'}
                        noMargin
                      />
                    </div>
                  )}
                  {shareIcons && (
                    <div className="mt-5">
                      <SocialShare inline hideShareCopy socialOptions={'all'} />
                    </div>
                  )}
                </animated.div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdditionalContent additionalContent={additionalContent} />
    </div>
  );
}
